import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Plus, Check, ArrowLeft, ArrowRight } from "lucide-react";
import { getProfile } from "../data/catalog";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

export default function ProfileServices() {
  const { id } = useParams();
  const navigate = useNavigate();
  const profile = getProfile(id);
  const { setActiveProfile, addItem, items } = useCart();

  useEffect(() => {
    if (profile) setActiveProfile(profile);
  }, [profile, setActiveProfile]);

  if (!profile) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-32 text-center">
        <h2 className="font-heading text-3xl">Profil introuvable</h2>
        <Link to="/" className="inline-block mt-6 underline">Retour à l'accueil</Link>
      </div>
    );
  }

  const isAdded = (sid) => items.some((i) => i.service_id === sid);

  const handleAdd = (svc) => {
    addItem(svc);
    toast.success(`${svc.name} ajouté au devis`);
  };

  return (
    <div data-testid="profile-page" className="max-w-7xl mx-auto px-6 py-16">
      {/* Header */}
      <Link to="/" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-8" data-testid="back-home">
        <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Retour aux profils
      </Link>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 items-start">
        <div className="lg:col-span-2">
          <div className="relative aspect-[4/5] overflow-hidden border border-[#1E2D4A]">
            <img src={profile.image} alt={profile.title} className="absolute inset-0 w-full h-full object-cover opacity-80" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#040914] via-transparent to-transparent" />
            <div className="absolute bottom-0 left-0 p-6">
              <div className="text-[10px] tracking-[0.3em] uppercase text-white/60">Profil sélectionné</div>
              <h1 className="font-heading text-3xl sm:text-4xl mt-2 tracking-tight">{profile.title}</h1>
              <p className="text-white/70 text-sm mt-1">{profile.subtitle}</p>
            </div>
          </div>
        </div>

        <div className="lg:col-span-3">
          <div className="text-xs tracking-[0.3em] uppercase text-white/40">02 — Services</div>
          <h2 className="font-heading text-3xl sm:text-4xl mt-2 tracking-tight mb-8">
            Ce que je peux dessiner pour vous
          </h2>

          <ul className="divide-y divide-[#1E2D4A] border-y border-[#1E2D4A]">
            {profile.services.map((svc) => (
              <li
                key={svc.id}
                data-testid={`service-row-${svc.id}`}
                className="flex items-center justify-between gap-4 py-5 hover:bg-[#0A1326]/60 px-2 transition-colors"
              >
                <div className="flex-1">
                  <div className="font-medium text-white">{svc.name}</div>
                  <div className="text-xs text-white/50 mt-1 tracking-wide">
                    {svc.min} – {svc.max} € TTC{svc.unit ? ` · l'${svc.unit}` : ""}
                  </div>
                </div>
                <button
                  data-testid={`add-service-${svc.id}`}
                  onClick={() => handleAdd(svc)}
                  className={`inline-flex items-center gap-2 px-4 py-2.5 text-xs tracking-wide uppercase border transition-all ${
                    isAdded(svc.id)
                      ? "bg-white text-black border-white"
                      : "bg-transparent text-white border-white/30 hover:border-white"
                  }`}
                >
                  {isAdded(svc.id) ? <Check className="w-4 h-4" strokeWidth={2} /> : <Plus className="w-4 h-4" strokeWidth={2} />}
                  {isAdded(svc.id) ? "Ajouté" : "Ajouter"}
                </button>
              </li>
            ))}
          </ul>

          <div className="mt-10 flex flex-wrap gap-4">
            <button
              data-testid="go-to-quote-btn"
              onClick={() => navigate("/devis")}
              disabled={items.length === 0}
              className="inline-flex items-center gap-2 bg-white text-black px-6 py-3 text-sm tracking-wide hover:bg-white/85 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              Voir mon devis ({items.length})
              <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </button>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 border border-white/30 text-white px-6 py-3 text-sm tracking-wide hover:border-white"
            >
              Besoin d'un autre service ?
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
